import moment from "moment-timezone"
import { competitionIdentity } from "../Competitions/CompetitionIdentity"
import { eventLabel } from "../../../EventDetailsOverview"
import { LEVELS } from "pages/Tenis10Claims/claimsLabels"

// The generic families, then every other catalogue product; the lanyards are Tenis10's (PRD 16 LR15).
const families = { balls: "Balls", medals: "Medals", diplomas: "Diplomas", water: "Water", lanyards: "Lanyards", other: "Other" }
export const familyName = family => families[family] || family
// "Roșu": a Tenis10 colour, the level of a Tenis10 competition and of the lines that follow its colour.
export const colourName = level => LEVELS[level] || level
// "Balls · Roșu": a generic line, by its family and, on a Tenis10 sheet, its colour.
export const genericName = ({ family, tenis10Level }) => [familyName(family), tenis10Level && colourName(tenis10Level)].filter(Boolean).join(" · ")

// Who made a logged change: "System", or the account's name.
export const actorName = actor => actor?.system ? "System" : [actor?.firstName, actor?.lastName].filter(Boolean).join(" ") || "Actor not recorded"
// "24 Sep 2026, 16:20": when a logged change was made, in the given time zone.
export const logTime = (value, timezone) => value && moment(value).isValid() ? moment(value).tz(timezone).format("DD MMM YYYY, HH:mm") : "Not recorded"

// "A, B and C".
export const joinNames = names => names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names.join("")

// "Balls, Medals, Diplomas and Water" for the generic lines of a sheet; a Tenis10 sheet's lines of each colour name their family once.
export const familyNames = (lines = []) => joinNames([...new Set(lines.map(line => familyName(line.family)))])

// Balls per match is 2, 3, 4 or "played" (the competition is played with used balls); a Tenis10 competition has none.
export const ballsPerMatchLabel = value => value == null ? "—" : value === "played" ? "Played balls" : String(value)

// "Level 4 · Doubles #5101" for a competition recorded in the history or on the sheet.
export const competitionName = (competition = {}) => competitionIdentity(competition).name
  + (competition.competitionId ? ` #${competition.competitionId}` : "")

// "1 team", "4 teams".
export const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`
// The catalogue's units, one and many: a `box` is a can of balls, and water counts bottles (Tudor, 2026-10-13). A unit without
// words, such as the litres water was counted in before, is written as it is stored.
const units = { box: ["can", "cans"], ball: ["ball", "balls"], pcs: ["pcs", "pcs"], set: ["set", "sets"], bottle: ["bottle", "bottles"] }
const wordsOf = unit => units[unit] || [unit || "", unit || ""]
// "can", "pcs": a unit as a label or a choice names it.
export const unitName = unit => wordsOf(unit)[0]

// "21 cans", "84 bottles", "—" without a quantity.
export const quantityLabel = (value, unit) => {
  if (value == null) return "—"
  const [one, many] = wordsOf(unit)
  return plural(value, one, many).trim()
}

// "1 case + 3 cans", "2 cases": a quantity in grouping packages, never rounded.
export const breakdownLabel = ({ packages, units: rest }, grouping, unit) => [plural(packages, grouping.name),
  ...(rest ? [quantityLabel(rest, unit)] : [])].join(" + ")

export const placeName = place => ({ 1: "Champion", 2: "Finalist" })[place] || `Place ${place}`

// The name a sheet line shows: its product or, for balls no product covers, the official ball or Balls TBD; a Tenis10 line of a colour
// without a product, its family and colour.
export const lineName = (line, officialBall) => line.product?.name
  || (line.family === "balls" && !line.tenis10Level ? (officialBall ? `Balls · ${officialBall}` : "Balls TBD") : genericName(line))

// The same product on several lines of a sheet (D21).
// "Medals Gold · already on the sheet": an offered product a line of the sheet holds, which comes as a line of its own.
export const offeredName = (name, onSheet) => (onSheet ? `${name} · already on the sheet` : name)
// What an offer of products chooses first: one not on the sheet, so that a second line of a product is always chosen on purpose.
export const firstOffer = entries => entries.find(entry => !entry.onSheet) || entries[0]
// Whether another line holds a line's product, and what a product's line numbered from 2 says instead of its rule: it calculates nothing.
const otherLineOf = (line, lines) => line.product && lines.find(other => other !== line && other.product?.id === line.product.id)
export const sharesProduct = (line, lines) => Boolean(otherLineOf(line, lines))
export const sameProductLabel = (line, lines) => {
  const other = otherLineOf(line, lines)
  if (!other) return "No calculated quantity"
  return `Same product as ${other.generic ? `the ${familyName(other.family).toLowerCase()} line` : "another line"}: no calculated quantity`
}

// "Medals Gold · Champion": a line, or one of its places, as the history recorded it.
export const loggedLineName = ({ family, tenis10Level, product, place } = {}) => [product?.name || genericName({ family, tenis10Level }),
  place && placeName(place)].filter(Boolean).join(" · ")

// A sheet's state, as the Logistics tab and Event details name it.
export const sheetStatuses = { inPreparation: { label: "In preparation", color: "warning" },
  readyForProcessing: { label: "Ready for processing", color: "info" }, readyToShip: { label: "Ready to be shipped", color: "primary" },
  shipped: { label: "Shipped", color: "success" }, noShipment: { label: "No shipment", color: "secondary" } }
export const supplierNames = { mpg: "MPG", club: "Club" }

// "Set for Medals Gold; the event now uses Medals Silver. …": a quantity set for a product an event edit replaced.
export const replacedProductLabel = ({ from, to }) => `Set ${from ? `for ${from.name}` : "without a catalogue product"}; the event now `
  + `${to ? `uses ${to.name}` : "has no catalogue product for this line"}. Set it again to confirm it.`

// The recommendation a rule shows on its line: "1 bottle per player per match" for still water; the minimum of the club's Tenis10 balls
// of a colour (R8, Tudor, 2026-10-21: "min de mingi necesare desfasurarii evenimentului, in conditiile in care o grupa va ocupa un teren").
export const ruleHint = (rule, unit) => rule?.type === "perPlayerPerMatch" ? `${quantityLabel(rule.quantity, unit)} per player per match`
  : rule?.type === "perGroup" ? `Recommendation: ${quantityLabel(rule.quantity, unit)} per group, one group per court. Provided by the club.`
  : null

export const componentNames = { mainDraw: "Main draw", groups: "Groups", phase: "Additional phase" }

// "1 player", "4 teams": confirmed Main List entries by game type; on a Tenis10 sheet, "8 children".
export const participantsLabel = (count, gameType, tenis10 = false) => tenis10 ? plural(count, "child", "children")
  : plural(count, gameType === "doubles" ? "team" : "player")
// "red": the balls of a Tenis10 colour, as the club knows them (the canvas verdict F-A, „8 red”).
const ballColours = { rosu: "red", portocaliu: "orange", verde: "green", galben: "yellow" }
export const ballColour = level => ballColours[level] || colourName(level)

export const matchSources = { real: { label: "Real", color: "soft-success" }, calculated: { label: "Calculated", color: "soft-info" },
  estimated: { label: "Estimated", color: "soft-warning" } }

export const gameTypeName = gameType => ({ singles: "Singles", doubles: "Doubles" })[gameType] || gameType

// "Singles · 2 groups · 10 of 12", "Doubles · Eliminatory · 4 teams of 8"; "Groups" when their number is not set.
export const competitionFormat = ({ gameType, drawModel, numberOfGroups, participants, capacity }) => [gameTypeName(gameType),
  drawModel === "groups" ? (numberOfGroups ? plural(numberOfGroups, "group") : "Groups") : "Eliminatory",
  `${gameType === "doubles" ? plural(participants, "team") : participants} of ${capacity}`].join(" · ")

// "4 groups × 4, Singles, Gold": the similar format an estimate read; a relaxed key is left out.
const estimateFormat = ({ groups, size, gameType, category }) => [size ? `${plural(groups, "group")} × ${size}` : plural(groups, "group"),
  gameType && gameTypeName(gameType), category && eventLabel(category)].filter(Boolean).join(", ")
const relaxedKeys = { category: "category", gameType: "game type", size: "group size" }

// "8 places estimated", "no phase estimated": an estimated additional phase in its row.
export const estimatedPlacesLabel = places => places ? `${plural(places, "place")} estimated` : "no phase estimated"

// Why an additional phase has the qualifying places it has (ticket 14, Q16): the level the
// estimate came from, how often the result occurred and when it was calculated.
export const estimateExplanation = ({ places, estimate }, organizer, timezone) => {
  const { level, format, dropped = [], competitions, frequency, organizerCompetitions, computedAt } = estimate
  const result = places ? plural(places, "qualification place") : "No additional phase"
  if (level === "fixed") return `${result} — no history: the smallest power of 2 at least the number of groups, at most the participants. To check.`
  const from = level === "organizer" ? `most frequent for organizer ${organizer?.name || "of the event"}`
    : `most frequent in the global estimate${dropped.length ? ` without ${joinNames(dropped.map(key => relaxedKeys[key] || key))}` : ""}`
  return [`${result} — ${from}: ${frequency} of ${plural(competitions, "similar competition")} (${estimateFormat(format)}, last 24 months).`,
    level !== "organizer" && organizerCompetitions != null && `The organizer has ${plural(organizerCompetitions, "similar competition")} of the minimum 3.`,
    computedAt && `Calculated on ${moment(computedAt).tz(timezone || "Europe/Bucharest").format("DD MMM YYYY")}.`].filter(Boolean).join(" ")
}
